import React, { useState } from 'react';
import ControlTierSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/ControlTierSelection';
import InitializationFormReview from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/InitializationFormReview';
import MachineNameSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/MachineNameSelection';
import MachineSizeSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/MachineSizeSelection';
import MachineStyleSelection from 'components/admin/deviceDashboard/deviceEditor/simulator/initialization/MachineStyleSelection';
// components
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { getMachineSizesForManufacturer } from 'api/admin/devices/devices';
import { useMachineControlTiers } from 'api/queries/useMachineControlTiers';

const AllianceSerialDeviceInitializationSimulator = (props) => {
  const { device, manufacturer, onBack, onSubmit, machineStyles } = props;
  const [loading, setLoading] = useState(false);
  const allianceInitializationScreens = {
    MACHINE_STYLE: 'MACHINE_STYLE',
    CONTROL_TIER: 'CONTROL_TIER',
    MACHINE_SIZE: 'MACHINE_SIZE',
    MACHINE_NAME: 'MACHINE_NAME',
    FINAL_REVIEW: 'FINAL_REVIEW',
  };
  const [initializationScreen, setInitializationScreen] = useState(
    allianceInitializationScreens.MACHINE_STYLE,
  );

  // form data
  const [selectedMachineStyle, setSelectedMachineStyle] = useState(null);
  const [selectedMachineStyleDisplay, setSelectedMachineStyleDisplay] = useState(null);
  const [selectedMachineType, setSelectedMachineType] = useState(null);
  const [selectedControlTier, setSelectedControlTier] = useState(null);
  const [selectedControlTierDisplay, setSelectedControlTierDisplay] = useState(null);
  const [machineSizes, setMachineSizes] = useState([]);
  const [selectedMachineSize, setSelectedMachineSize] = useState(null);
  const [selectedMachineSizeDisplay, setSelectedMachineSizeDisplay] = useState(null);
  const [selectedMachineName, setSelectedMachineName] = useState(null);

  const {
    data: { controlTiers } = { controlTiers: [] },
    isLoading: isLoadingControlTiers,
    refetch: fetchMachineControlTiers,
  } = useMachineControlTiers({
    style: selectedMachineStyleDisplay,
    manufacturer,
    enabled: false,
  });

  /**
   * For manufacturers that support machine size, if you go back from Machine Name selection
   * we should only show the Machine Size selection if there are available sizes for that manufacturer
   * and style
   *
   * If not, then we should take you back to control tier selection
   */
  const handleGoBackFromMachineNameForSizeManufacturers = () => {
    if (machineSizes.length) {
      setInitializationScreen(allianceInitializationScreens.MACHINE_SIZE);
    } else {
      setInitializationScreen(allianceInitializationScreens.CONTROL_TIER);
    }
  };

  /**
   * navigate back to machine size selection
   */
  const handleGoBackForMachineNameSelection = () => {
    if (['ALLIANCE-ACA', 'ALLIANCE-CENT'].includes(manufacturer)) {
      return handleGoBackFromMachineNameForSizeManufacturers();
    } else {
      setInitializationScreen(allianceInitializationScreens.MACHINE_STYLE);
    }
  };

  /**
   * Depending on the current wizard screen, set the proper screen to show
   */
  const handleWizardGoBackFunctionality = () => {
    switch (initializationScreen) {
      case allianceInitializationScreens.MACHINE_STYLE:
        return onBack();

      case allianceInitializationScreens.CONTROL_TIER:
        return setInitializationScreen(allianceInitializationScreens.MACHINE_STYLE);

      case allianceInitializationScreens.MACHINE_SIZE:
        return setInitializationScreen(allianceInitializationScreens.CONTROL_TIER);

      case allianceInitializationScreens.MACHINE_NAME:
        return handleGoBackForMachineNameSelection();

      case allianceInitializationScreens.FINAL_REVIEW:
        return setInitializationScreen(allianceInitializationScreens.MACHINE_NAME);

      default:
        return onBack();
    }
  };

  /**
   * disable the submit functionality if any of the following is missing or null:
   *
   * 1) manufacturer
   * 2) machine style
   * 3) machine type
   * 4) machine name
   */
  const disableFinalSubmitButton = () => {
    return !manufacturer || !selectedMachineStyle || !selectedMachineType || !selectedMachineName;
  };

  /**
   * compile the form data and send back to DeviceSimulator
   */
  const submitDeviceInitializationFormData = () => {
    const finalData = {
      deviceId: device.id,
      manufacturer,
      machineStyle: selectedMachineStyle,
      machineType: selectedMachineType,
      machineSize: selectedMachineSize,
      controlTier: selectedControlTier,
      lowerMachineName: selectedMachineName,
    };

    return onSubmit(finalData);
  };

  /**
   * render a summary screen for the user to review before submitting
   */
  const renderFinalDataReview = () => {
    return (
      <InitializationFormReview
        manufacturer={manufacturer}
        selectedMachineStyleDisplay={selectedMachineStyleDisplay}
        selectedControlTierDisplay={selectedControlTierDisplay}
        selectedMachineSizeDisplay={selectedMachineSizeDisplay}
        selectedMachineName={selectedMachineName}
        selectedMachineType={selectedMachineType}
        onBack={handleWizardGoBackFunctionality}
        onNext={submitDeviceInitializationFormData}
        disableFinalSubmitButton={disableFinalSubmitButton}
      />
    );
  };

  /**
   * navigate to the final review screen when moving forward after machine name selection
   */
  const handleNextOnMachineNameSelection = () => {
    setInitializationScreen(allianceInitializationScreens.FINAL_REVIEW);
  };

  /**
   * render the various control tiers that can be selected for a given machine manufacturer
   */
  const renderMachineNameSelection = () => {
    return (
      <MachineNameSelection
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNextOnMachineNameSelection}
        selectedMachineName={selectedMachineName}
        setSelectedMachineName={setSelectedMachineName}
        title={"Choose your machine's name"}
      />
    );
  };

  /**
   * Hide the current mazhine size screen and show the machine name selection screen
   */
  const handleNextOnMachineSizeSelection = () => {
    setInitializationScreen(allianceInitializationScreens.MACHINE_NAME);
  };

  /**
   * save the selected machine size rawValue in state
   *
   * @param {object} machineSizeData
   */
  const handleMachineSizeSelection = (machineSizeData) => {
    setSelectedMachineSize(machineSizeData.rawValue);
    setSelectedMachineSizeDisplay(machineSizeData.displayValue);
  };

  /**
   * render the various control tiers that can be selected for a given machine manufacturer
   */
  const renderMachineSizeSelection = () => {
    return (
      <MachineSizeSelection
        machineSizes={machineSizes}
        selectedMachineSizeDisplay={selectedMachineSizeDisplay}
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNextOnMachineSizeSelection}
        onValueChange={handleMachineSizeSelection}
        selectedMachineSize={selectedMachineSize}
      />
    );
  };

  /**
   * when navigating next on control tier selection, we need to fetch the machine sizes for the
   * manufacturer and control tier selected, and then render the machine size selection UI
   */
  const handleNextOnControlTierSelection = async () => {
    try {
      setLoading(true);
      const { data } = await getMachineSizesForManufacturer(
        manufacturer,
        selectedMachineStyleDisplay,
      );
      setMachineSizes(data.sizes);
      setInitializationScreen(allianceInitializationScreens.MACHINE_SIZE);
    } catch (error) {
      setMachineSizes([]);
      setInitializationScreen(allianceInitializationScreens.MACHINE_NAME);
    } finally {
      setLoading(false);
    }
  };

  /**
   * save the selected control tier rawValue in state
   *
   * @param {object} controlTierData
   */
  const handleControlTierSelection = (controlTierData) => {
    setSelectedControlTier(controlTierData.rawValue);
    setSelectedControlTierDisplay(controlTierData.displayValue);

    // setting these as null because control tier selection often determines the sizes applicable
    setSelectedMachineSize(null);
    setSelectedMachineSizeDisplay(null);
  };

  /**
   * render the various control tiers that can be selected for a given machine manufacturer
   */
  const renderControlTierSelection = () => {
    return (
      <ControlTierSelection
        isLoading={isLoadingControlTiers}
        controlTiers={controlTiers}
        selectedControlTierDisplay={selectedControlTierDisplay}
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNextOnControlTierSelection}
        onValueChange={handleControlTierSelection}
        selectedControlTier={selectedControlTier}
      />
    );
  };

  /**
   * set the selected machine style and then based on the displayValue of machine style,
   * set the machine type
   *
   * @param {object} machineStyleData
   */
  const handleMachineStyleSelection = (machineStyleData) => {
    setSelectedMachineStyle(machineStyleData.rawValue);
    setSelectedMachineStyleDisplay(machineStyleData.displayValue);

    // setting these as null because machine style often determines the sizes applicable
    setSelectedControlTier(null);
    setSelectedControlTierDisplay(null);
    setSelectedMachineSize(null);
    setSelectedMachineSizeDisplay(null);

    if (machineStyleData.displayValue === 'Dryer' || machineStyleData.displayValue === 'Tumbler') {
      setSelectedMachineType('DRYER');
    } else {
      setSelectedMachineType('WASHER');
    }
  };

  /**
   * when clicking next after selecting machine style, perform the following logic:
   *
   * 1) if ACA or centurion are selected manufacturers, show the control tier selection
   * 2) if ACA, centurion, or midas are selected manufacturers, show the machine size selection
   * and fetch the machine sizes for the given manufacturer and control tier
   * 3) if MDC is the selected manufacturer, show the form option for selecting machine name
   */
  const handleNextOnMachineStyleSelection = () => {
    if (['ALLIANCE-ACA', 'ALLIANCE-CENT'].includes(manufacturer)) {
      fetchMachineControlTiers();
      setInitializationScreen(allianceInitializationScreens.CONTROL_TIER);
    } else {
      setInitializationScreen(allianceInitializationScreens.MACHINE_NAME);
    }
  };

  /**
   * render the dropdown and wizard view for machine style selection
   */
  const renderMachineStyleSelection = () => {
    return (
      <MachineStyleSelection
        machineStyles={machineStyles}
        selectedMachineStyleDisplay={selectedMachineStyleDisplay}
        onBack={handleWizardGoBackFunctionality}
        onNext={handleNextOnMachineStyleSelection}
        onValueChange={handleMachineStyleSelection}
        selectedMachineStyle={selectedMachineStyle}
      />
    );
  };

  /**
   * Determine the proper form/wizard step based on state of the component
   */
  const renderInitializationWizardOptions = () => {
    switch (initializationScreen) {
      case allianceInitializationScreens.MACHINE_STYLE:
        return renderMachineStyleSelection();

      case allianceInitializationScreens.CONTROL_TIER:
        return renderControlTierSelection();

      case allianceInitializationScreens.MACHINE_SIZE:
        return renderMachineSizeSelection();

      case allianceInitializationScreens.MACHINE_NAME:
        return renderMachineNameSelection();

      case allianceInitializationScreens.FINAL_REVIEW:
        return renderFinalDataReview();

      default:
        return renderMachineStyleSelection();
    }
  };

  return <>{loading ? <CentsLoader /> : renderInitializationWizardOptions()}</>;
};

export default AllianceSerialDeviceInitializationSimulator;
